import React, { useState, useEffect } from "react";
import TableGrid from "modules/swiftboard/components/TableGrid";
import TableToolBar from "modules/swiftboard/components/TableToolBar";
import { Calendar, Trash2, ShieldCheck, Shield, Briefcase, User, CheckCircle, XCircle } from "lucide-react";
import LocalDateTime from "services/date-converter";
import Pagination from "modules/swiftboard/components/Pagination";
import sortData from "utils/sorting-coloum";
import { useAlert } from "context/AlertContext";
import ConfirmDialogue from "components/ui/ConfirmDialogue";
import NoDataState from "modules/swiftboard/components/NoDataState";
import apiLoginHistory from "./api";
import Avatar from "components/ui/Avatar";
import { GlobalAdvanceFilter } from "modules/swiftboard/components/advance-filter";
import { loginAuditFilterFields } from "../features/login-audit.filter";

const SORT_OPTIONS = [
    { value: "created_date_desc", label: "Newest first" },
    { value: "created_date_asc", label: "Oldest first" },
];

const csvCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const EMPTY_FILTERS = {};

const LoginHistory = ({ onTotalCountChange }) => {
    const { showAlert } = useAlert();

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [resetTrigger, setResetTrigger] = useState(0);
    const [filters, setFilters] = useState(EMPTY_FILTERS);

    const [sortConfig, setSortConfig] = useState({
        key: "created_date",
        direction: "desc",
    });
    const [sortValue, setSortValue] = useState("created_date_desc");
    const [resetSelectionTrigger, setResetSelectionTrigger] = useState(0);
    const [exporting, setExporting] = useState(false);
    const [confirmData, setConfirmData] = useState({
        isOpen: false,
        title: "",
        message: "",
        confirmText: "Yes",
        cancelText: "No",
        icon: null,
        resolve: null,
    });

    const fetchCountries = async () => {
        setLoading(true);
        try {
            const payload = {
                searchText: searchText,
                skip: (currentPage - 1) * pageSize,
                limit: pageSize,
                is_success: true,
                search: JSON.stringify(filters),
            };


            const response = await apiLoginHistory.getAllLoginHistory(payload);

            if (response.data.status) {
                setData(response.data.data);
                setTotalCount(response.data.total_count || 0);
                onTotalCountChange?.(response.data.total_count || 0);
            }
            else {
                showAlert({ type: 'warning', message: response.data?.message });
            }
        } catch (error) {
            console.error("Error fetching projects:", error);
            if (error.response) {
                showAlert({ type: 'error', message: error.response?.data?.message });
            } else {
                showAlert({ type: 'error', message: "An unexpected error occurred. Please try again!" });
            }
        }
        finally {
            setLoading(false);
        }
    }

    const handleDeleteSelected = async (ids = []) => {

        if (ids.length === 0) {
            showAlert({ type: 'warning', message: "Please select at least one country to delete." });
            return;
        }

        const confirmed = await openConfirm({
            title: "Delete Login History",
            message:
                ids.length > 1
                    ? `Are you sure you want to delete ${ids.length} selected Login History? This action cannot be undone.`
                    : "Are you sure you want to delete this Login History? This action cannot be undone.",
            confirmText: "Delete",
            cancelText: "Cancel",
            icon: <Trash2 size={24} className="text-danger" />,
        });

        if (!confirmed) return;
        try {
            const response = await apiLoginHistory.deleteLoginHistory(ids)

            if (response.data.status) {
                showAlert({ type: 'success', message: response.data?.message });

                fetchCountries();
                setSelectedRows([]);
                setResetSelectionTrigger((prev) => prev + 1);
            }
            else {
                showAlert({ type: 'warning', message: response.data?.message });
            }
        }
        catch (error) {
            if (error.response) {
                showAlert({ type: 'error', message: error.response?.data?.message });
            } else {
                showAlert({ type: 'error', message: "An unexpected error occurred. Please try again!" });
            }
        }
    };

    const handleClearSelection = () => {
        setSelectedRows([]);
        setResetSelectionTrigger((prev) => prev + 1);
    };

    const handleSortChange = (value) => {
        setSortValue(value);
        setSortConfig({ key: "created_date", direction: value.endsWith("_desc") ? "desc" : "asc" });
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const response = await apiLoginHistory.getAllLoginHistory({
                searchText,
                skip: 0,
                limit: totalCount || 1000,
                is_success: true,
                search: JSON.stringify(filters),
            });
            const rows = response.data?.data || [];

            if (rows.length === 0) {
                showAlert({ type: "warning", message: "There is no data to export." });
                return;
            }

            const header = ["Audit ID", "User", "Email", "Auth Method", "IP Address", "Status", "Login Time", "Response"];
            const lines = rows.map((row) =>
                [
                    row.id,
                    row.user ? `${row.user.first_name || ""} ${row.user.last_name || ""}`.trim() : "Unknown User",
                    row.email,
                    row.auth_method,
                    row.ip_address,
                    row.success ? "Success" : "Failed",
                    row.created_date,
                    row.response_message,
                ]
                    .map(csvCell)
                    .join(",")
            );
            const csv = [header.map(csvCell).join(","), ...lines].join("\n");
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = "login-history.csv";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            showAlert({ type: "error", message: error.response?.data?.message || "Failed to export data" });
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => {
        fetchCountries();
    }, [currentPage, pageSize, searchText, filters, resetTrigger]);

    useEffect(() => {
        const sorted = sortData(data, sortConfig);
        setFilteredData(sorted);
    }, [data, sortConfig]);

    const openConfirm = (options) => {
        return new Promise((resolve) => {
            setConfirmData({
                ...options,
                isOpen: true,
                resolve,
            });
        });
    };

    const handleConfirmClose = (result) => {
        if (confirmData.resolve) {
            confirmData.resolve(result);
        }

        setConfirmData((prev) => ({
            ...prev,
            isOpen: false,
            resolve: null,
        }));
    };
    const handleSearch = (value) => {
        setSearchText(value)
        setCurrentPage(1); // reset to first page
    };

    const hasActiveQuery = !!searchText || Object.keys(filters).length > 0;

    const handleApplyFilters = ({ search }) => {
        setFilters(search);
        setCurrentPage(1);
    };

    // Handle page change
    const handlePageChange = (page, newPageSize = pageSize) => {
        setCurrentPage(page);
        if (newPageSize !== pageSize) {
            setPageSize(newPageSize);
            setCurrentPage(1);
        }
    };



    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    };

    const handleCreate = () => {
        console.log("Open create country modal");
        showAlert({ type: 'success', message: 'create country' })
    };


    const roleConfig = {
        SUPER_ADMIN: {
            icon: ShieldCheck,
            className: "bg-primary_tint text-primary border-primary",
            label: "Super Admin",
        },
        ADMIN: {
            icon: Shield,
            className: "bg-danger_bg text-danger border-danger_border",
            label: "Admin",
        },
        CLIENT: {
            icon: Briefcase,
            className: "bg-info_bg text-info border-info_border",
            label: "Client",
        },
        EMPLOYEE: {
            icon: User,
            className: "bg-success_bg text-success border-success_border",
            label: "Employee",
        },
    };

    const handleRefresh = () => {
        setSearchText('');// to reset search text
        setFilters(EMPTY_FILTERS);// to reset advanced filters
        setCurrentPage(1);// to reset page number to 1
        // Bumping resetTrigger clears the search bar and filter drawer, and refetches through
        // the effect once the cleared state is in place (a direct fetch here would send the old one).
        setResetTrigger(prev => prev + 1);
    };

    const columns = [
        {
            key: "id",
            label: "Audit ID",
            width: "110px",
            sortable: true,
            render: (row) => (
                <span className="px-3 py-1 text-xs font-semibold rounded-full bg-surface_alt text-text_primary border border-border">
                    #{row.id}
                </span>
            ),
        },
        {
            key: "user",
            label: "User",
            width: "250px",
            sortable: true,
            render: (row) => (
                <div className="flex items-center gap-3">
                    <div className="relative">
                        {row?.user?.profile_picture_url ? (
                            <img
                                src={row.user.profile_picture_url}
                                alt={`${row.user.first_name} ${row.user.last_name}`}
                                className="w-10 h-10 rounded-full object-cover border-2 border-surface shadow"
                            />
                        ) : (
                            <Avatar user={row.user} size={36} rounded />
                        )}
                        {row?.user?.is_online && (
                            <span className="absolute bottom-0 right-0 w-3 h-3 bg-success border-2 border-surface rounded-full"></span>
                        )}
                    </div>

                    <div className="flex flex-col">
                        <span className="text-sm font-semibold text-text_primary">
                            {row?.user
                                ? `${row.user.first_name} ${row.user.last_name}`
                                : "Unknown User"}
                        </span>
                        <span className="text-xs text-text_muted">
                            {row.email}
                        </span>
                    </div>
                </div>
            ),
        },
        {
            key: "user_role_fid",
            label: "Role Name",
            width: "220px",
            sortable: true,
            render: (row) => {
                const config = roleConfig[row?.user?.user_role?.role] || "-";
                const Icon = config.icon;

                return (
                    <div
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${config.className}`}
                    >
                        <Icon size={14} />
                        {config.label}
                    </div>
                );
            },
        },
        {
            key: "auth_method",
            label: "Authentication",
            width: "160px",
            sortable: true,
            render: (row) => (
                <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full bg-primary_tint text-primary border border-primary">
                    <ShieldCheck size={14} />
                    {row.auth_method || "N/A"}
                </span>
            ),
        },
        {
            key: "device",
            label: "Device / Browser",
            width: "250px",
            render: (row) => (
                <div className="flex flex-col">
                    <span className="text-xs font-medium text-text_primary truncate max-w-[220px]">
                        {row.browser || "Unknown Browser"}
                        {row.browser_version ? ` v${row.browser_version}` : ""}
                    </span>
                    <span className="text-xs text-text_muted truncate max-w-[220px]">
                        {row.user_agent || "N/A"}
                    </span>
                </div>
            ),
        },
        {
            key: "location",
            label: "Location",
            width: "220px",
            render: (row) => (
                <div className="flex flex-col text-xs text-text_secondary">
                    <span>
                        {row.city || row.region || row.country
                            ? `${row.city || ""} ${row.region ? ", " + row.region : ""} ${row.country ? ", " + row.country : ""}`
                            : "Unknown Location"}
                    </span>
                    <span className="text-text_muted">
                        IP: {row.ip_address || "N/A"}
                    </span>
                </div>
            ),
        },
        {
            key: "success",
            label: "Status",
            width: "130px",
            sortable: true,
            render: (row) => (
                <span
                    className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full border ${row.success
                        ? "bg-success_bg text-success border-success_border"
                        : "bg-danger_bg text-danger border-danger_border"
                        }`}
                >
                    {row.success ? (
                        <>
                            <CheckCircle size={14} /> Success
                        </>
                    ) : (
                        <>
                            <XCircle size={14} /> Failed
                        </>
                    )}
                </span>
            ),
        },
        {
            key: "created_date",
            label: "Login Time",
            width: "180px",
            sortable: true,
            render: (row) => (
                <div className="flex items-center gap-2 text-xs text-text_primary">
                    <Calendar size={14} className="text-text_muted" />
                    <LocalDateTime utcTime={row.created_date} type="datetime" />
                </div>
            ),
        },
        {
            key: "response_message",
            label: "Response",
            width: "220px",
            render: (row) => (
                <span
                    className="inline-block max-w-[200px] truncate px-3 py-1 text-xs font-medium rounded-lg bg-surface_alt text-text_primary border border-border"
                    title={row.response_message}
                >
                    {row.response_message || "N/A"}
                </span>
            ),
        },

    ];

    // Custom action buttons for each row
    const renderRowActions = (row) => (
        <div className="flex items-center gap-1.5 justify-end">

            <button
                onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteSelected([row.id]);
                }}
                className="
    group inline-flex items-center gap-2
    px-3 py-1.5 rounded-xl
    bg-danger_bg text-danger
    border border-danger_border
    hover:bg-danger hover:text-surface hover:border-danger
    shadow-sm hover:shadow-md
    active:scale-95
    transition-all duration-200
  "
            >
                <Trash2
                    size={15}
                    className="transition-transform duration-200 group-hover:rotate-6"
                />
                <span className="text-xs font-semibold">Delete</span>
            </button>
        </div>
    );

    return (
        <div className="h-full overflow-y-auto bg-surface_alt">
            <div className="flex flex-col gap-4 pb-4">
                <TableToolBar
                    onSearch={handleSearch}
                    onRefresh={handleRefresh}
                    onCreate={handleCreate}
                    onDeleteSelected={() => handleDeleteSelected(selectedRows)}
                    selectedCount={selectedRows.length}
                    resetSearchTrigger={resetTrigger}
                    canCreate={false}
                    resultCount={totalCount}
                    sortOptions={SORT_OPTIONS}
                    sortValue={sortValue}
                    onSortChange={handleSortChange}
                    onClearSelection={handleClearSelection}
                    onExport={handleExport}
                    exporting={exporting}
                    advanceFilter={
                        <GlobalAdvanceFilter
                            fields={loginAuditFilterFields}
                            onApply={handleApplyFilters}
                            resetKey={resetTrigger}
                        />
                    }
                />

                {data?.length === 0 && !loading ? (<div className="flex items-center justify-center border rounded-lg border-border bg-surface min-h-[320px]">
                    <NoDataState
                        icon="search"
                        title="No Login History Found"
                        message={hasActiveQuery ? "No login history matches your search or filters." : "There is no login history data available."}
                        showRefresh={false}
                        showBack={false}
                        size="medium"
                    />
                </div>) : (<>
                    <div className="overflow-hidden border rounded-lg border-border">
                        <TableGrid
                            data={filteredData}
                            totalCount={totalCount}
                            loading={loading}
                            currentPage={currentPage}
                            columns={columns}
                            sortConfig={sortConfig}
                            onSort={handleSort}
                            onSelectionChange={setSelectedRows}
                            pageSize={pageSize}
                            selectable={true}
                            renderActions={renderRowActions}
                            resetSelectionTrigger={resetSelectionTrigger}
                        />
                    </div>

                    <Pagination
                        totalCount={totalCount}
                        currentPage={currentPage}
                        pageSize={pageSize}
                        onPageChange={handlePageChange}
                    />


                    {confirmData.isOpen && (
                        <ConfirmDialogue
                            confirmData={confirmData}
                            handleConfirmClose={handleConfirmClose}
                        />
                    )}
                </>)}

            </div>
        </div>
    );
};

export default LoginHistory;